/**
 * Assurance Service Layer
 *
 * This service provides a clean API for fetching assurance data.
 * Currently uses mock data, but designed to be easily replaced with real API calls.
 */

import type {
  AlignedSignal,
  InstitutionRow,
  TrailData,
  QuickLookData,
  TimelineSnapshot,
  CompareDiff,
  SearchResult,
  Topic,
} from '../types/assurance';

import {
  mockAlignedSignals,
  mockInstitutions,
  mockQuickLookData,
  mockTrailData,
  mockTimelineSnapshots,
  mockCompareDiffs,
  getInstitutionById as mockGetInstitutionById,
  getSignalByTopic as mockGetSignalByTopic,
  search as mockSearch,
} from '../data/assurance.mock';

/**
 * Simulates network delay for realistic UX
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Assurance Service
 *
 * All methods return Promises to simulate async API calls.
 * In production, replace mock calls with actual fetch() calls.
 */
export const assuranceService = {
  /**
   * Fetch all aligned signals
   */
  async getAlignedSignals(): Promise<AlignedSignal[]> {
    await delay(100);
    return [...mockAlignedSignals];
  },

  /**
   * Fetch signal by topic
   */
  async getSignalByTopic(topic: Topic): Promise<AlignedSignal | undefined> {
    await delay(50);
    return mockGetSignalByTopic(topic);
  },

  /**
   * Fetch all institutions
   */
  async getInstitutions(): Promise<InstitutionRow[]> {
    await delay(150);
    return [...mockInstitutions];
  },

  /**
   * Fetch institution by ID
   */
  async getInstitutionById(id: string): Promise<InstitutionRow | undefined> {
    await delay(50);
    return mockGetInstitutionById(id);
  },

  /**
   * Fetch Quick Look data for a topic
   */
  async getQuickLook(topic: Topic): Promise<QuickLookData> {
    await delay(30);
    return mockQuickLookData[topic];
  },

  /**
   * Fetch Assurance Trail data for a topic
   */
  async getTrailData(topic: Topic): Promise<TrailData> {
    await delay(80);
    return mockTrailData[topic];
  },

  /**
   * Fetch timeline snapshots
   */
  async getTimelineSnapshots(): Promise<TimelineSnapshot[]> {
    await delay(120);
    return [...mockTimelineSnapshots];
  },

  /**
   * Create a new timeline snapshot
   */
  async createSnapshot(
    label: string,
    signals: AlignedSignal[],
    institutions: InstitutionRow[]
  ): Promise<TimelineSnapshot> {
    await delay(100);

    const snapshot: TimelineSnapshot = {
      id: `snap_${Date.now()}`,
      label,
      timestamp: new Date().toISOString(),
      signals: [...signals],
      institutions: [...institutions],
    };

    // In production, this would POST to API
    mockTimelineSnapshots.push(snapshot);

    return snapshot;
  },

  /**
   * Fetch compare diffs between two states
   */
  async getCompareDiffs(
    _beforeId?: string,
    _afterId?: string
  ): Promise<CompareDiff[]> {
    await delay(100);
    // In production, this would calculate diffs based on snapshot IDs
    return [...mockCompareDiffs];
  },

  /**
   * Search across all entities
   */
  async search(query: string): Promise<SearchResult[]> {
    await delay(80);
    return mockSearch(query);
  },

  /**
   * Fetch historical data for timeline scrubber
   * Returns data for a specific point in time
   */
  async getHistoricalState(timestamp: string): Promise<{
    signals: AlignedSignal[];
    institutions: InstitutionRow[];
  }> {
    await delay(150);

    // In production, this would fetch historical state from API
    // For now, simulate by slightly modifying current state
    const historicalSignals = mockAlignedSignals.map((signal) => ({
      ...signal,
      value: Math.max(0, signal.value - Math.random() * 10),
      updatedAt: timestamp,
    }));

    const historicalInstitutions = mockInstitutions.map((inst) => ({
      ...inst,
      updatedAt: timestamp,
    }));

    return {
      signals: historicalSignals,
      institutions: historicalInstitutions,
    };
  },

  /**
   * Get available timeline range
   * Returns the earliest and latest timestamps for historical data
   */
  async getTimelineRange(): Promise<{ start: string; end: string }> {
    await delay(50);

    // In production, this would query API for data availability
    const end = new Date().toISOString();
    const start = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(); // 1 year ago

    return { start, end };
  },
};

export default assuranceService;
